// frontend/src/components/SettingsLayout.jsx
/**
 * SettingsLayout — shared shell for personal account settings pages.
 *
 * Provides a two-column layout:
 *   left  │  narrow sidebar with settings sub-navigation
 *   right │  the page content via <Outlet>
 *
 * Routes wrapped by this layout:
 *   /settings                → AccountSettingsPage
 *   /settings/notifications  → NotificationPrefsPage
 *   /settings/webhooks       → WebhooksPage  (admin+ only)
 *
 * Organisational settings (Org, API Keys, Billing) are accessed via the main
 * sidebar and keep their own standalone layouts.
 */

import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// ─── Nav item definitions ─────────────────────────────────────────────────── //

const NAV_ITEMS = [
  {
    to: "/settings",
    end: true,
    label: "Account",
    adminOnly: false,
    // Heroicons outline user-circle
    icon: "M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  {
    to: "/settings/notifications",
    end: false,
    label: "Notifications",
    adminOnly: false,
    // Heroicons outline bell
    icon:
      "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 " +
      "00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 " +
      ".538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
  },
  {
    to: "/settings/webhooks",
    end: false,
    label: "Webhooks",
    adminOnly: true,
    // Heroicons outline arrow-path (represents delivery/retry)
    icon: "M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────── //

function isAdminOrOwner(role) {
  return role === "owner" || role === "admin";
}

const navClass = ({ isActive }) =>
  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
    isActive
      ? "bg-blue-50 text-blue-700"
      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
  }`;

// ─── Layout ───────────────────────────────────────────────────────────────── //

export default function SettingsLayout() {
  const { currentOrg } = useAuth();
  const role = currentOrg?.role;

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.adminOnly || isAdminOrOwner(role),
  );

  return (
    <div className="flex gap-8 min-h-full">
      {/* ── Settings sidebar ────────────────────────────────────────── */}
      <aside className="hidden sm:block w-44 shrink-0">
        <p className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          Settings
        </p>
        <nav className="space-y-0.5">
          {visibleItems.map(({ to, end, label, icon }) => (
            <NavLink key={to} to={to} end={end} className={navClass}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                className="w-4 h-4 shrink-0"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
              </svg>
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* ── Page content ────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
